import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { api } from '../api.js'
import { useAuth } from '../auth/AuthContext.jsx'

// Admin: add/edit a participant on ANY user's account — standalone page.
// Routes: /admin/users/:uid/participants/new
//         /admin/users/:uid/participants/:pid/edit

const blank = { name: '', is_self: false, birthdate: '', phone: '', email: '', notes: '' }

export function AdminParticipantFormPage() {
  const { t } = useTranslation()
  const { uid, pid } = useParams()
  const navigate = useNavigate()
  const { user: me, loading: authLoading } = useAuth()
  const isEdit = Boolean(pid)

  const [owner, setOwner] = useState(null)
  const [v, setV] = useState(blank)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  const set = (key) => (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value
    setV((cur) => ({ ...cur, [key]: val }))
  }

  useEffect(() => {
    if (authLoading || !me || me.role !== 'admin') return
    api.admin.users.get(uid)
      .then((d) => {
        setOwner(d.user)
        if (isEdit) {
          const p = (d.participants || []).find((x) => String(x.id) === String(pid))
          if (!p) { setError('not_found'); return }
          setV({
            name: p.name || '', is_self: Number(p.is_self) === 1,
            birthdate: p.birthdate || '', phone: p.phone || '',
            email: p.email || '', notes: p.notes || '',
          })
        }
      })
      .catch((e) => setError(e.code || 'server'))
      .finally(() => setLoading(false))
  }, [authLoading, me, uid, pid, isEdit])

  async function submit(e) {
    e.preventDefault()
    setBusy(true); setError(null)
    const payload = {
      name: v.name.trim(),
      is_self: v.is_self,
      birthdate: v.birthdate || null,
      phone: v.phone || null,
      email: v.email || null,
      notes: v.notes || null,
    }
    try {
      if (isEdit) await api.admin.participants.update(pid, payload)
      else        await api.admin.participants.create(uid, payload)
      navigate(`/admin/users/${uid}`, { replace: true })
    } catch (err) {
      setError(err.code || 'server')
      setBusy(false)
    }
  }

  if (authLoading || loading) return <main className="container"><p>{t('common.loading')}</p></main>
  if (!me || me.role !== 'admin') {
    return <main className="container"><p className="error">{t('admin.errors.not_admin')}</p></main>
  }

  return (
    <main className="container">
      <p><Link to={`/admin/users/${uid}`}>← {owner ? owner.name : t('admin_users.back_to_list')}</Link></p>
      <h1>
        {isEdit ? t('participants.edit_title') : t('participants.add_title')}
        {owner && <span className="muted"> — {owner.name}</span>}
      </h1>

      {error && (
        <p className="error">
          {t(`participants.errors.${error}`, { defaultValue: t(`admin_users.errors.${error}`, { defaultValue: error }) })}
        </p>
      )}

      <section className="card form-card">
        <form onSubmit={submit} noValidate>
          <label>
            <span>{t('participants.name')}</span>
            <input type="text" value={v.name} onChange={set('name')} required minLength={2} />
          </label>
          <label className="checkbox-label">
            <input type="checkbox" checked={!!v.is_self} onChange={set('is_self')} />
            <span>{t('participants.is_self')}</span>
          </label>
          {/* Self participant: email/birthdate/phone come from the account. */}
          {v.is_self ? (
            <p className="muted small">{t('participants.self_fields_hint')}</p>
          ) : (
            <>
              <label>
                <span>{t('participants.birthdate')}</span>
                <input type="date" value={v.birthdate} onChange={set('birthdate')} />
              </label>
              <label>
                <span>{t('participants.phone')}</span>
                <input type="tel" value={v.phone} onChange={set('phone')} />
              </label>
              <label>
                <span>{t('participants.email')}</span>
                <input type="email" value={v.email} onChange={set('email')} />
              </label>
            </>
          )}
          <label>
            <span>{t('participants.notes')}</span>
            <textarea value={v.notes} onChange={set('notes')} rows={3} />
          </label>
          <div className="form-actions">
            <button type="submit" disabled={busy}>{busy ? t('common.loading') : t('common.save')}</button>
            <Link to={`/admin/users/${uid}`} className="link-button">{t('common.cancel')}</Link>
          </div>
        </form>
      </section>
    </main>
  )
}
